import { type ReactLocalization, useLocalization } from "@fluent/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Row } from "@narmi/design_system";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { DateInput } from "@lending/components/DateInput/DateInput";
import { PageHeader } from "@lending/components/PageHeader";
import { TextInput } from "@lending/components/TextInput/TextInput";
import { useStartVerifiedIdentityChallengeMutation } from "@lending/hooks/queries/VerifiedIdentityChallenge/useStartVerifiedIdentityChallengeMutation";
import { useNavigateWithSearch } from "@lending/hooks/useNavigateWithSearch";
import { useToastsContext } from "@lending/hooks/useToasts";
import styles from "../PortalLogin.module.css";
import { usePortalLogin } from "../PortalLoginContext";

function createIdentitySchema(l10n: ReactLocalization) {
  const requiredMessage = l10n.getString("error-required", null, "Required");

  return z.object({
    date_of_birth: z
      .string()
      .min(1, { message: requiredMessage })
      .refine(
        (value) => {
          const [year, month, day] = value.split("-").map(Number);
          const date = new Date(year, month - 1, day);
          return (
            date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
          );
        },
        {
          message: l10n.getString(
            "portal-login-invalid-date-of-birth",
            null,
            "Enter a valid date of birth"
          ),
        }
      ),
    tax_id_last_four: z.string().regex(/^[0-9]{4}$/, {
      message: l10n.getString(
        "portal-login-invalid-tax-id-last-four",
        null,
        "Enter the last 4 digits of your Social Security Number"
      ),
    }),
  });
}

type IdentityFormData = z.infer<ReturnType<typeof createIdentitySchema>>;

/**
 * First step of the Loan Portal login: collect date of birth and tax ID last
 * four, then start the identity challenge.
 *
 * Whether any application matched is deliberately not distinguishable here. The
 * backend collapses "no match" and other failures into one generic 400, and this
 * screen renders a single generic message for every failure so the form cannot
 * be used to probe which identities exist.
 */
export function IdentityScreen() {
  const { l10n } = useLocalization();
  const navigate = useNavigateWithSearch();
  const { setChallenge } = usePortalLogin();
  const { showErrorToast } = useToastsContext();

  const { mutateAsync: startChallenge, isPending } = useStartVerifiedIdentityChallengeMutation();

  const { handleSubmit, control } = useForm<IdentityFormData>({
    defaultValues: { date_of_birth: "", tax_id_last_four: "" },
    resolver: zodResolver(createIdentitySchema(l10n)),
  });

  async function onSubmit(data: IdentityFormData) {
    try {
      const result = await startChallenge({ body: data });

      setChallenge({
        challengeId: result.id,
        emailOptions: result.email_options.map((option) => ({
          maskedEmail: option.masked_email,
          token: option.token,
        })),
        // Present only when a single email matched and the code was sent straight
        // away; the multi-email path resolves this once an option is chosen.
        selectedMaskedEmail: result.masked_email ?? null,
        demoCode: result.code ?? null,
      });

      void navigate(result.status === "code_sent" ? "verify" : "select-email");
    } catch {
      showErrorToast(
        l10n.getString(
          "portal-login-generic-error",
          null,
          "We couldn't verify that information. Please check your details and try again."
        )
      );
    }
  }

  return (
    <>
      <PageHeader
        heading={l10n.getString("portal-login-identity-heading", null, "Find your application")}
        subheading={l10n.getString(
          "portal-login-identity-subheading",
          null,
          "Confirm your details and we'll email you a verification code."
        )}
      />

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className={styles.formFields}>
          <DateInput
            name="date_of_birth"
            control={control}
            label={l10n.getString("personal-info-dob-label", null, "Date of birth")}
          />
          <TextInput
            name="tax_id_last_four"
            control={control}
            label={l10n.getString("portal-login-tax-id-last-four-label", null, "Last 4 of SSN")}
            inputMode="numeric"
            transform={(value) => value.replace(/\D/g, "").slice(0, 4)}
          />
        </div>

        <div className={styles.encryptionNotice}>
          <span className="narmi-icon-lock" aria-hidden="true" />
          <span>
            {l10n.getString(
              "portal-login-encryption-notice",
              null,
              "We use 256-bit encryption to protect your information."
            )}
          </span>
        </div>

        <div className="margin--top--xxl">
          <Row alignItems="center" justifyContent="space-between">
            <BackButton
              onClick={() => {
                void navigate("/product-selection");
              }}
            />
            <Button
              label={l10n.getString("button-label-continue", null, "Continue")}
              type="submit"
              isLoading={isPending}
            />
          </Row>
        </div>
      </form>
    </>
  );
}
